import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { Flame, Snowflake, Lock, CheckCircle, RefreshCw, Trophy, Star, Award } from 'lucide-react';

const CATEGORY_LABELS = {
  milestone: 'Milestones',
  streak: 'Streaks',
  learning: 'Learning',
  engagement: 'Engagement',
  sabbath: 'Sabbath'
};

const CATEGORY_ORDER = ['milestone', 'streak', 'learning', 'engagement', 'sabbath'];

// ─── Coin component ───────────────────────────────────────────────────────────
const Coin = ({ type, size = 'md' }) => {
  const sizes = { sm: 'w-5 h-5 text-xs', md: 'w-8 h-8 text-sm', lg: 'w-12 h-12 text-lg' };
  const isGold = type === 'gold';
  return (
    <div className={`${sizes?.[size]} rounded-full flex items-center justify-center font-black shadow-md flex-shrink-0 ${
      isGold
        ? 'bg-gradient-to-br from-yellow-300 via-amber-400 to-yellow-600 border-2 border-yellow-500 text-yellow-900' :'bg-gradient-to-br from-gray-300 via-slate-400 to-gray-600 border-2 border-gray-500 text-gray-800'
    }`}>
      {isGold ? '✦' : '✧'}
    </div>
  );
};

// ─── Progress bar for repeatable awards ──────────────────────────────────────
const RepeatableProgress = ({ def, timesEarned }) => {
  if (!def?.repeatable || !def?.threshold) return null;

  const triggerLabels = {
    TAG_MILESTONE: 'tags',
    SESSION_MILESTONE: 'sessions',
    REFLECTION_MILESTONE: 'reflections',
    VERSE_MILESTONE: 'verses',
    WEEKLY_VERSE_STREAK: 'weekly streaks',
    WEEKLY_VERSE_STREAK_2X: 'double-verse weeks',
    VERSE_MASTERED: 'verses mastered',
    READING_PLAN_COMPLETED: 'plans completed',
  };
  const unit = triggerLabels?.[def?.trigger_event] || 'completions';
  const completed = timesEarned || 0;
  const nextTarget = (completed + 1) * def?.threshold;

  return (
    <div className="mt-2 w-full">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-muted-foreground">
          {completed > 0 ? `${completed}× earned` : 'Not yet earned'}
        </span>
        <span className="text-xs text-primary font-medium">
          Next: {nextTarget} {unit}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all"
          style={{ width: completed > 0 ? '100%' : '0%' }}
        />
      </div>
    </div>
  );
};

// ─── Streak Status Card ───────────────────────────────────────────────────────
const StreakStatusCard = ({ userId }) => {
  const [streakData, setStreakData] = useState(null);
  const [goldBalance, setGoldBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [streakRes, balRes] = await Promise.all([
          supabase?.from('user_streaks')?.select('current_streak, longest_streak, last_session_date')?.eq('user_id', userId)?.maybeSingle(),
          supabase?.from('user_talent_balance')?.select('gold_balance')?.eq('user_id', userId)?.maybeSingle()
        ]);
        setStreakData(streakRes?.data);
        setGoldBalance(balRes?.data?.gold_balance || 0);
      } catch (err) {
        console.error('StreakStatusCard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  if (loading) return <div className="h-24 bg-muted rounded-xl animate-pulse" />;

  const currentStreak = streakData?.current_streak || 0;
  const longestStreak = streakData?.longest_streak || 0;
  const lastDate = streakData?.last_session_date;
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const yesterday = new Date(Date.now() - 86400000)?.toISOString()?.split('T')?.[0];
  const isAtRisk = lastDate && lastDate < yesterday;
  const isActive = lastDate === today || lastDate === yesterday;
  const missedDays = lastDate
    ? Math.max(0, Math.floor((new Date(today) - new Date(lastDate)) / 86400000) - 1)
    : 0;
  const canFreeze = goldBalance >= missedDays && missedDays > 0;

  return (
    <div className={`rounded-xl border-2 p-4 ${
      isAtRisk
        ? 'bg-destructive/10 border-destructive/30'
        : currentStreak > 0
        ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30' :'bg-muted border-border'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className={`w-5 h-5 ${currentStreak > 0 ? 'text-orange-400' : 'text-muted-foreground'}`} />
          <span className="font-semibold text-foreground text-sm">Streak Status</span>
        </div>
        {isAtRisk && (
          <span className="text-xs bg-destructive/20 text-destructive border border-destructive/30 px-2 py-0.5 rounded-full font-medium">
            ⚠️ At Risk
          </span>
        )}
        {isActive && !isAtRisk && (
          <span className="text-xs bg-success/20 text-success border border-success/30 px-2 py-0.5 rounded-full font-medium">
            ✓ Active
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center">
          <div className={`text-3xl font-black ${currentStreak > 0 ? 'text-orange-400' : 'text-muted-foreground'}`}>
            {currentStreak}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">Current Streak</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-black text-primary">{longestStreak}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Best Streak</div>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-card/50 rounded-lg px-3 py-2 border border-border/50">
        <Snowflake className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {missedDays > 0 ? (
            <p className="text-xs text-muted-foreground">
              {missedDays} day{missedDays > 1 ? 's' : ''} missed —{' '}
              {canFreeze
                ? <span className="text-blue-400 font-medium">spend {missedDays} 🪙 gold to freeze</span>
                : <span className="text-destructive font-medium">not enough gold to freeze</span>
              }
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-primary">{goldBalance} gold</span> available for streak freezes
              <span className="text-muted-foreground/60"> (1 gold = 1 missed day)</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
const AwardsSummaryCard = ({ balance, earnedCount, totalCount, allAwards, userAwards }) => {
  const goldBalance = balance?.gold_balance ?? 0;
  const silverBalance = balance?.silver_balance ?? 0;
  const totalGoldEarned = balance?.total_gold_earned ?? 0;
  const totalSilverEarned = balance?.total_silver_earned ?? 0;

  const earnedMap = {};
  userAwards?.forEach(a => { earnedMap[a.award_slug] = a; });

  const categoryStats = CATEGORY_ORDER?.map(cat => {
    const catAwards = allAwards?.filter(d => d?.category === cat);
    const catEarned = catAwards?.filter(d => earnedMap?.[d?.slug])?.length;
    return { cat, label: CATEGORY_LABELS?.[cat] || cat, earned: catEarned, total: catAwards?.length };
  })?.filter(c => c?.total > 0);

  const completionPct = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;

  return (
    <div className="bg-gradient-to-br from-amber-500/15 via-card to-card rounded-2xl border border-amber-500/30 p-6 shadow-warm-lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 bg-gradient-to-br from-primary to-amber-600 rounded-full flex items-center justify-center shadow-warm-md">
          <Award className="w-5 h-5 text-primary-foreground" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Talents &amp; Awards Summary</h2>
          <p className="text-xs text-muted-foreground">Your spiritual growth achievements</p>
        </div>
        <div className="ml-auto text-right">
          <div className="text-2xl font-black text-primary">{completionPct}%</div>
          <div className="text-xs text-muted-foreground">Complete</div>
        </div>
      </div>

      {/* Talent Balances */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
          <div className="flex justify-center mb-2">
            <Coin type="gold" size="lg" />
          </div>
          <div className="text-3xl font-black text-primary">{goldBalance}</div>
          <div className="text-sm font-semibold text-amber-400 mt-1">Gold Talents</div>
          <div className="text-xs text-muted-foreground mt-1">{totalGoldEarned} earned lifetime</div>
        </div>
        <div className="bg-gradient-to-br from-slate-500/20 to-gray-500/10 border border-slate-500/30 rounded-xl p-4 text-center">
          <div className="flex justify-center mb-2">
            <Coin type="silver" size="lg" />
          </div>
          <div className="text-3xl font-black text-muted-foreground">{silverBalance}</div>
          <div className="text-sm font-semibold text-slate-400 mt-1">Silver Talents</div>
          <div className="text-xs text-muted-foreground mt-1">{totalSilverEarned} earned lifetime</div>
        </div>
      </div>

      {/* Silver → Gold conversion */}
      <div className="bg-muted/50 border border-border rounded-xl p-3 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Coin type="silver" size="sm" />
            <span className="text-xs font-medium text-muted-foreground">Silver → Gold Conversion</span>
          </div>
          <span className="text-xs font-bold text-primary">{silverBalance % 7}/7 silver</span>
        </div>
        <div className="h-2 bg-border rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-slate-400 to-primary rounded-full transition-all duration-500"
            style={{ width: `${((silverBalance % 7) / 7) * 100}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 text-center">
          {(7 - (silverBalance % 7)) === 7
            ? '✨ Conversion ready! Next silver will auto-convert.'
            : `${7 - (silverBalance % 7)} more silver → 1 gold (auto-converts at 7)`}
        </p>
      </div>

      {/* Awards progress */}
      <div className="bg-muted/50 border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Awards Progress</span>
          </div>
          <span className="text-sm font-bold text-primary">{earnedCount}/{totalCount}</span>
        </div>
        {/* Overall progress bar */}
        <div className="h-2.5 bg-border rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-gradient-to-r from-primary to-amber-400 rounded-full transition-all duration-700"
            style={{ width: `${completionPct}%` }}
          />
        </div>
        {/* Category breakdown */}
        <div className="space-y-2">
          {categoryStats?.map(({ cat, label, earned, total }) => {
            const pct = total > 0 ? Math.round((earned / total) * 100) : 0;
            return (
              <div key={cat} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-20 flex-shrink-0">{label}</span>
                <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-10 text-right flex-shrink-0">{earned}/{total}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TalentsAndAwards = ({ userId }) => {
  const [balance, setBalance] = useState(null);
  const [allAwards, setAllAwards] = useState([]);
  const [userAwards, setUserAwards] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async (silent = false) => {
    if (!userId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [balRes, defsRes, earnedRes] = await Promise.all([
        supabase?.from('user_talent_balance')?.select('*')?.eq('user_id', userId)?.maybeSingle(),
        supabase?.from('award_definitions')?.select('*')?.order('sort_order', { ascending: true }),
        supabase?.from('user_awards')?.select('*')?.eq('user_id', userId)
      ]);

      // If no balance row exists yet, create one so the UI shows 0 instead of blank
      if (!balRes?.data && !balRes?.error) {
        await supabase
          ?.from('user_talent_balance')
          ?.upsert({ user_id: userId }, { onConflict: 'user_id', ignoreDuplicates: true });
        // Re-fetch after upsert
        const { data: freshBal } = await supabase
          ?.from('user_talent_balance')
          ?.select('*')
          ?.eq('user_id', userId)
          ?.maybeSingle();
        setBalance(freshBal);
      } else {
        setBalance(balRes?.data);
      }

      setAllAwards(defsRes?.data || []);
      setUserAwards(earnedRes?.data || []);
    } catch (err) {
      console.error('Failed to load awards:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadData();
  }, [userId, loadData]);

  // Refresh when tab/window regains focus so admin-granted talents appear
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userId) {
        loadData(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, loadData]);

  const earnedMap = {};
  userAwards?.forEach(a => { earnedMap[a.award_slug] = a; });

  const filteredAwards = allAwards?.filter(def => {
    if (filter === 'earned') return !!earnedMap?.[def?.slug];
    if (filter === 'locked') return !earnedMap?.[def?.slug];
    return true;
  });

  const groupedByCategory = CATEGORY_ORDER?.reduce((acc, cat) => {
    const items = filteredAwards?.filter(d => d?.category === cat);
    if (items?.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const earnedCount = userAwards?.length;
  const totalCount = allAwards?.length;

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-48" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2]?.map(i => <div key={i} className="h-32 bg-muted rounded-xl" />)}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3]?.map(i => <div key={i} className="h-24 bg-muted rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Summary Card ────────────────────────────────────────────────── */}
      <AwardsSummaryCard
        balance={balance}
        earnedCount={earnedCount}
        totalCount={totalCount}
        allAwards={allAwards}
        userAwards={userAwards}
      />

      {/* ── Streak Status ───────────────────────────────────────────────── */}
      <div className="bg-card rounded-2xl border border-border p-6 shadow-warm">
        <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-400" />
          Streak &amp; Freeze
        </h2>
        <StreakStatusCard userId={userId} />
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Complete a review session daily to build your streak. Gold talents act as streak freezes.
        </p>
      </div>

      {/* ── Awards Gallery ──────────────────────────────────────────────── */}
      <div className="bg-card rounded-2xl border border-border p-6 shadow-warm">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" />
            Awards Gallery
          </h2>
          <div className="flex items-center gap-2">
            {/* Manual refresh button */}
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              title="Refresh awards"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              {['all', 'earned', 'locked']?.map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                    filter === f
                      ? 'bg-card text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f}
                  {f === 'earned' && (
                    <span className="ml-1 text-primary">({earnedCount})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {Object.keys(groupedByCategory)?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{filter === 'earned' ? 'No awards earned yet. Keep going!' : 'No awards found.'}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByCategory)?.map(([cat, defs]) => (
              <div key={cat}>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span>{CATEGORY_LABELS?.[cat] || cat}</span>
                  <span className="text-border font-normal">
                    ({defs?.filter(d => earnedMap?.[d?.slug])?.length}/{defs?.length})
                  </span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {defs?.map(def => {
                    const earned = earnedMap?.[def?.slug];
                    const isEarned = !!earned;
                    const isRepeatable = def?.repeatable;

                    return (
                      <div
                        key={def?.slug}
                        className={`rounded-xl border p-3 text-center transition-all relative ${
                          isEarned
                            ? 'bg-gradient-to-br from-amber-500/15 to-yellow-500/10 border-amber-500/40 shadow-warm'
                            : 'bg-muted/50 border-border'
                        }`}
                      >
                        {!isEarned && (
                          <div className="absolute top-2 right-2">
                            <Lock className="w-3 h-3 text-muted-foreground/50" />
                          </div>
                        )}
                        {isRepeatable && isEarned && (
                          <div className="absolute top-2 right-2">
                            <RefreshCw className="w-3 h-3 text-primary" />
                          </div>
                        )}
                        <div className={`text-3xl mb-2 ${!isEarned ? 'grayscale opacity-30' : ''}`}>
                          {def?.icon}
                        </div>
                        <div className={`text-xs font-bold leading-tight mb-1 ${isEarned ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                          {def?.name}
                        </div>
                        <div className={`text-xs leading-tight ${isEarned ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}>
                          {def?.description}
                        </div>
                        {(def?.gold_reward > 0 || def?.silver_reward > 0) && (
                          <div className="flex justify-center gap-1 mt-2 flex-wrap">
                            {def?.gold_reward > 0 && (
                              <span className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-full font-medium">
                                🪙 {def?.gold_reward}
                              </span>
                            )}
                            {def?.silver_reward > 0 && (
                              <span className="text-xs bg-slate-500/20 text-slate-400 border border-slate-500/30 px-1.5 py-0.5 rounded-full font-medium">
                                🥈 {def?.silver_reward}
                              </span>
                            )}
                          </div>
                        )}
                        {isEarned ? (
                          <div className="mt-2">
                            <div className="flex items-center justify-center gap-1 text-xs text-success font-semibold">
                              <CheckCircle className="w-3 h-3" />
                              <span>
                                Earned{earned?.times_earned > 1 ? ` ×${earned?.times_earned}` : ''}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {new Date(earned.earned_at)?.toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric'
                              })}
                            </div>
                            {isRepeatable && def?.threshold && (
                              <RepeatableProgress def={def} timesEarned={earned?.times_earned} />
                            )}
                          </div>
                        ) : (
                          <div className="mt-2">
                            <div className="text-xs text-muted-foreground/50">
                              {def?.threshold
                                ? `Requires: ${def?.threshold}`
                                : 'Complete the action'}
                            </div>
                            {isRepeatable && def?.threshold && (
                              <RepeatableProgress def={def} timesEarned={0} />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentsAndAwards;
